import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import SmoothScroll from "@/components/layout/SmoothScroll";
import NoiseOverlay from "@/components/layout/NoiseOverlay";
import CustomCursor from "@/components/layout/CustomCursor";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import FloatingWhatsApp from "@/components/layout/FloatingWhatsApp";

import Landing from "@/pages/Landing";
import featureFlags from "@/config/featureFlags.json";
import CasosDeExito from "@/pages/CasosDeExito";
import CasoDetalle from "@/pages/CasoDetalle";
import NormaDetalle from "@/pages/NormaDetalle";
import ServicioDetalle from "@/pages/ServicioDetalle";
import Recursos from "@/pages/Recursos";
import RecursoDetalle from "@/pages/RecursoDetalle";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import Seo from "@/components/seo/Seo";
import { trackPageView } from "@/lib/analytics";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSiteContent from "@/pages/admin/AdminSiteContent";
import {
  AdminCasos, AdminServices, AdminNormas, AdminClients,
  AdminTestimonials, AdminFaqs, AdminResources, AdminSectors, AdminRequests,
} from "@/pages/admin/AdminCollections";

function ScrollAndAnchor() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let cancelled = false;
      let frameId;
      const timers = [];

      const scrollToAnchor = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (!el) return;
        const headerOffset = 72;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
      };

      const scheduleScroll = () => {
        cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(scrollToAnchor);
      };

      // Dynamic API sections and images above the anchor can change page height
      // after the first render. Keep the anchor aligned while layout settles.
      scheduleScroll();
      [100, 300, 700, 1400, 2400].forEach((delay) => timers.push(setTimeout(scheduleScroll, delay)));
      window.addEventListener("load", scheduleScroll, { once: true });

      const observer = new ResizeObserver(scheduleScroll);
      observer.observe(document.body);

      const stopFollowingAnchor = () => { cancelled = true; observer.disconnect(); };
      const interactionEvents = ["wheel", "touchstart", "pointerdown", "keydown"];
      interactionEvents.forEach((event) => window.addEventListener(event, stopFollowingAnchor, { once: true, passive: true }));
      const observerTimer = setTimeout(() => observer.disconnect(), 2600);

      return () => {
        cancelled = true;
        cancelAnimationFrame(frameId);
        timers.forEach(clearTimeout);
        clearTimeout(observerTimer);
        observer.disconnect();
        window.removeEventListener("load", scheduleScroll);
        interactionEvents.forEach((event) => window.removeEventListener(event, stopFollowingAnchor));
      };
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
}

function PublicShell() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

function AdminSeo() {
  const { pathname } = useLocation();
  if (!pathname.startsWith("/admin")) return null;
  return <Seo title="Administración" description="Área privada de KB Consultoría." path={pathname} noindex />;
}

function AnalyticsPageViews() {
  const { pathname, search } = useLocation();
  useEffect(() => { trackPageView(`${pathname}${search}`); }, [pathname, search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <SmoothScroll>
              <NoiseOverlay />
              <CustomCursor />
              <ScrollAndAnchor />
              <AdminSeo />
              <AnalyticsPageViews />
              <Toaster position="bottom-center" theme="light" />
              <Routes>
                {/* Public routes with shared shell */}
                <Route element={<PublicShell />}>
                  <Route path="/" element={<Landing />} />
                  {featureFlags.showSuccessCases ? (
                    <>
                      <Route path="/casos-de-exito" element={<CasosDeExito />} />
                      <Route path="/casos-de-exito/:slug" element={<CasoDetalle />} />
                    </>
                  ) : (
                    <Route path="/casos-de-exito/*" element={<Navigate to="/" replace />} />
                  )}
                  <Route path="/normas/isoiec-27001" element={<Navigate to="/normas/iso-27001" replace />} />
                  <Route path="/normas/:slug" element={<NormaDetalle />} />
                  <Route path="/servicios/:slug" element={<ServicioDetalle />} />
                  <Route path="/recursos" element={<Recursos />} />
                  <Route path="/recursos/:slug" element={<RecursoDetalle />} />
                  <Route path="/politica-privacidad" element={<LegalPage titulo="Política de privacidad" cuerpo={<PrivacyBody />} />} />
                  <Route path="/politica-cookies" element={<LegalPage titulo="Política de cookies" cuerpo={<CookiesBody />} />} />
                  <Route path="/terminos" element={<LegalPage titulo="Términos y condiciones" cuerpo={<TermsBody />} />} />
                </Route>

                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="casos" element={<AdminCasos />} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="normas" element={<AdminNormas />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="faqs" element={<AdminFaqs />} />
                  <Route path="resources" element={<AdminResources />} />
                  <Route path="sectors" element={<AdminSectors />} />
                  <Route path="contact_requests" element={<AdminRequests />} />
                  <Route path="site" element={<AdminSiteContent />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </SmoothScroll>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function PrivacyBody() {
  return (
    <>
      <p>Esta política describe cómo KB Consultoría recopila, utiliza y protege la información personal que usted proporciona a través del formulario de contacto.</p>
      <p><strong>Datos recopilados:</strong> nombre, correo, teléfono, empresa, país, ciudad y descripción de la necesidad.</p>
      <p><strong>Finalidad:</strong> atender su solicitud, elaborar propuestas y mantener comunicación relacionada con nuestros servicios.</p>
      <p><strong>Conservación:</strong> conservamos los datos durante el tiempo necesario para atender la consulta y cumplir obligaciones legales.</p>
      <p><strong>Derechos:</strong> puede solicitar acceso, rectificación o supresión escribiendo a nuestro correo de contacto.</p>
      <p>Este documento es editable desde el panel administrativo del sitio.</p>
    </>
  );
}

function CookiesBody() {
  return (
    <>
      <p>Este sitio puede utilizar cookies técnicas para su funcionamiento y cookies analíticas para medir el uso y mejorar la experiencia.</p>
      <p>Puede configurar su navegador para bloquear o eliminar cookies. Este documento es editable desde el panel administrativo.</p>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <p>El contenido de este sitio es propiedad de KB Consultoría. Su uso se realiza bajo términos de referencia informativos.</p>
      <p>La utilización de los servicios se rige por el contrato específico firmado con cada cliente.</p>
    </>
  );
}

export default App;
